/**
 * Route tree (project-guidelines.md §2.2).
 *
 * Phase 4 wires the patient surface (profile, family, addresses, prescriptions, uploads)
 * and the doctor surface (Rx history, create-Rx wizard, Rx detail/edit) under responsive
 * role shells. Pharmacist + support remain placeholder dashboards until their phases.
 */

import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { RequireRole } from "./auth/RequireRole";
import { SessionExpiry } from "./auth/SessionExpiry";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { PushNotifications } from "./components/PushNotifications";
import { DOCTOR_RX_CREATION_ENABLED } from "./lib/features";
import { Landing } from "./routes/Landing";
import { AdminLayout } from "./routes/admin/AdminLayout";
import { AuditViewer } from "./routes/admin/AuditViewer";
import { DeletionReview } from "./routes/admin/DeletionReview";
import { KpiDashboard } from "./routes/admin/KpiDashboard";
import { TopMedicines } from "./routes/admin/TopMedicines";
import { RequestAnalytics } from "./routes/staff/RequestAnalytics";
import { SupportInbox } from "./routes/staff/SupportInbox";
import { SupportLayout } from "./routes/staff/SupportLayout";
import { SupportProfile } from "./routes/staff/SupportProfile";
import { Doctors } from "./routes/admin/Doctors";
import { Medicines } from "./routes/admin/Medicines";
import { Orders as AdminOrders } from "./routes/admin/Orders";
import { PharmacySettings } from "./routes/admin/PharmacySettings";
import { Refunds } from "./routes/admin/Refunds";
import { Staff } from "./routes/admin/Staff";
import { StatusMaster } from "./routes/admin/StatusMaster";
import { Transactions } from "./routes/admin/Transactions";
import { ChangePassword } from "./routes/auth/ChangePassword";
import { Login } from "./routes/auth/Login";
import { Signup } from "./routes/auth/Signup";
import { StaffLogin } from "./routes/auth/StaffLogin";
import { CreateRx } from "./routes/doctor/CreateRx";
import { DoctorLayout } from "./routes/doctor/DoctorLayout";
import { DoctorPrescriptions } from "./routes/doctor/DoctorPrescriptions";
import { DoctorProfile } from "./routes/doctor/DoctorProfile";
import { DoctorRxDetail } from "./routes/doctor/DoctorRxDetail";
import { AccountDeletion } from "./routes/legal/AccountDeletion";
import { Addresses } from "./routes/patient/Addresses";
import { ConsentGate } from "./routes/patient/ConsentGate";
import { EmergencyContacts } from "./routes/patient/EmergencyContacts";
import { Family } from "./routes/patient/Family";
import { Notifications } from "./routes/patient/Notifications";
import { OrderDetail } from "./routes/patient/OrderDetail";
import { Orders } from "./routes/patient/Orders";
import { PatientLayout } from "./routes/patient/PatientLayout";
import { Settings } from "./routes/patient/Settings";
import { Support } from "./routes/patient/Support";
import { PrescriptionDetail } from "./routes/patient/PrescriptionDetail";
import { Prescriptions } from "./routes/patient/Prescriptions";
import { Reminders } from "./routes/patient/Reminders";
import { Profile } from "./routes/patient/Profile";
import { Uploads } from "./routes/patient/Uploads";
import { Digitise } from "./routes/pharmacist/Digitise";
import { DigitiseUpload } from "./routes/pharmacist/DigitiseUpload";
import { PharmacistLayout } from "./routes/pharmacist/PharmacistLayout";
import { PharmacistOrderDetail } from "./routes/pharmacist/PharmacistOrderDetail";
import { Queue } from "./routes/pharmacist/Queue";
import { Placeholder } from "./components/Placeholder";

export function App() {
  const location = useLocation();
  return (
    <>
      {/* Watches the JWT exp; on expiry it signs out and routes to the landing page. */}
      <SessionExpiry />
      {/* Registers the browser for Firebase web push while signed in (no-op in the WebView shell). */}
      <PushNotifications />

      {/* A render error in one screen shows a recoverable message; navigating away clears it. */}
      <ErrorBoundary key={location.pathname}>
        <Routes>
          {/* Public entry — pick a role (admin/support), then route through the auth guards. */}
          <Route path="/" element={<Landing />} />

          {/* Auth (public) — unified phone-first entry. /auth serves the roles picked on the
              Landing page; patient, doctor and pharmacist sign in at their own URL, typed
              directly in the browser (/patient is declared with the patient surface below). */}
          <Route path="/auth" element={<Login />} />
          <Route path="/doctor" element={<Login role="doctor" />} />
          <Route path="/pharmacist" element={<Login role="pharmacist" />} />
          <Route path="/auth/change-password" element={<ChangePassword />} />
          {/* Staff sign-up from the admin's SMS link: /auth/signup?token=… (ADM-01). */}
          <Route path="/auth/signup" element={<Signup />} />
          {/* Email staff login kept as a secondary entry point. */}
          <Route path="/auth/staff" element={<StaffLogin />} />

          {/* Account deletion for any user, no sign-in (DPDP P-16): the app-store deletion
              URL and the path the mobile app's Legal screen shows. */}
          <Route path="/legal/data-deletion" element={<AccountDeletion />} />
          <Route path="/delete-account" element={<Navigate to="/legal/data-deletion" replace />} />

          {/* Legal (public, no auth) — P-19 */}
          <Route
            path="/legal/*"
            element={<Placeholder title="Legal" surface="legal" />}
          />

          {/* Patient — P-01..P-19. /patient itself is the patient sign-in page (a signed-in
              patient is sent on to /patient/prescriptions); the surface lives beneath it. */}
          <Route path="/patient">
            <Route index element={<Login role="patient" />} />
            <Route
              element={
                <RequireRole role="patient">
                  <ConsentGate>
                    <PatientLayout />
                  </ConsentGate>
                </RequireRole>
              }
            >
              <Route path="prescriptions" element={<Prescriptions />} />
              <Route path="prescriptions/:id" element={<PrescriptionDetail />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
              <Route path="reminders" element={<Reminders />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="support" element={<Support />} />
              {/* A notification's link to one ticket (support.reply / support.resolved, ACCT-11). */}
              <Route path="support/:id" element={<Support />} />
              <Route path="settings" element={<Settings />} />
              <Route path="uploads" element={<Uploads />} />
              <Route path="family" element={<Family />} />
              <Route path="emergency-contacts" element={<EmergencyContacts />} />
              <Route path="addresses" element={<Addresses />} />
              <Route path="profile" element={<Profile />} />
            </Route>
          </Route>

          {/* Doctor — D-01..D-04 (signs in at /doctor) */}
          <Route
            path="/staff/doctor"
            element={
              <RequireRole role="doctor">
                <DoctorLayout />
              </RequireRole>
            }
          >
            <Route index element={<Navigate to="prescriptions" replace />} />
            <Route path="prescriptions" element={<DoctorPrescriptions />} />
            <Route path="prescriptions/:id" element={<DoctorRxDetail />} />
            {/* Writing Rx is switched off for now (lib/features.ts, D-08): the URL lands on the list. */}
            <Route
              path="create"
              element={DOCTOR_RX_CREATION_ENABLED ? <CreateRx /> : <Navigate to="/staff/doctor/prescriptions" replace />}
            />
            <Route path="profile" element={<DoctorProfile />} />
          </Route>

          {/* Pharmacist — Rx-01..Rx-17 (signs in at /pharmacist) */}
          <Route
            path="/staff/pharmacist"
            element={
              <RequireRole role="pharmacist">
                <PharmacistLayout />
              </RequireRole>
            }
          >
            <Route index element={<Navigate to="queue" replace />} />
            <Route path="queue" element={<Queue />} />
            <Route path="orders/:id" element={<PharmacistOrderDetail />} />
            <Route path="uploads" element={<Digitise />} />
            <Route path="uploads/:id" element={<DigitiseUpload />} />
          </Route>

          <Route
            path="/staff/admin"
            element={
              <RequireRole role="admin">
                <AdminLayout />
              </RequireRole>
            }
          >
            <Route index element={<Navigate to="medicines" replace />} />
            <Route path="medicines" element={<Medicines />} />
            <Route path="status-master" element={<StatusMaster />} />
            <Route path="pharmacy-settings" element={<PharmacySettings />} />
            <Route path="doctors" element={<Doctors />} />
            <Route path="staff" element={<Staff />} />
            {/* Every order to track, and every payment and refund with the filing reports (A-16). */}
            <Route path="orders" element={<AdminOrders />} />
            <Route path="transactions" element={<Transactions />} />
            <Route path="refunds" element={<Refunds />} />
            <Route path="support" element={<SupportInbox />} />
            <Route path="audit" element={<AuditViewer />} />
            <Route path="kpi" element={<KpiDashboard />} />
            {/* The top 100 sellers and any medicine's rank (?days=&sort=&medicine=, A-02). */}
            <Route path="kpi/top-medicines" element={<TopMedicines />} />
            <Route path="deletions" element={<DeletionReview />} />
          </Route>

          <Route
            path="/staff/support"
            element={
              <RequireRole role="support">
                <SupportLayout />
              </RequireRole>
            }
          >
            <Route index element={<Navigate to="inbox" replace />} />
            <Route path="inbox" element={<SupportInbox />} />
            {/* A notification's link to one ticket (support.ticket.opened, ACCT-11). */}
            <Route path="tickets/:id" element={<SupportInbox />} />
            <Route path="analytics" element={<RequestAnalytics />} />
            <Route path="profile" element={<SupportProfile />} />
          </Route>

          <Route
            path="*"
            element={<Placeholder title="Not found" surface="404" />}
          />
        </Routes>
      </ErrorBoundary>
    </>
  );
}
